import { useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import { isTauri } from '@/lib/utils/tauri';
import { useUIPreferencesStore } from '@/store/ui-preferences.store';

const logger = createLogger('hooks/useTauriLiquidGlass');

const SIDEBAR_VARIANT = 1;
const FALLBACK_THEME_HEX = '#1a181b';
const LIGHT_TINT_ALPHA = 0xc0;
const DARK_TINT_ALPHA = 0xd0;
const DARK_MODE_DARKEN_FACTOR = 0.28;
const LIGHT_MODE_WHITE_BLEND_FACTOR = 0.34;
const LIGHT_MODE_ON_DARK_SYSTEM_WHITE_BLEND_FACTOR = 0.72;
const LIGHT_MODE_ON_DARK_SYSTEM_TINT_ALPHA = 0xc8;

function normalizeHexColor(value: string): string | null {
  const normalized = value.trim();
  if (!normalized) return null;

  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
    return normalized.toLowerCase();
  }

  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return null;
}

function withAlpha(hex: string, alpha: number): string {
  return `${hex}${alpha.toString(16).padStart(2, '0')}`;
}

function darkenHexColor(hex: string, factor: number): string {
  const clampedFactor = Math.min(Math.max(factor, 0), 1);
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * (1 - clampedFactor));
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * (1 - clampedFactor));
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * (1 - clampedFactor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

function blendHexColors(baseHex: string, mixHex: string, ratio: number): string {
  const clampedRatio = Math.min(Math.max(ratio, 0), 1);
  const mix = (baseChannel: string, mixChannel: string) => {
    const base = parseInt(baseChannel, 16);
    const mixed = parseInt(mixChannel, 16);
    return Math.round(base + (mixed - base) * clampedRatio);
  };

  const r = mix(baseHex.slice(1, 3), mixHex.slice(1, 3));
  const g = mix(baseHex.slice(3, 5), mixHex.slice(3, 5));
  const b = mix(baseHex.slice(5, 7), mixHex.slice(5, 7));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

export function useTauriLiquidGlass(): void {
  const tauriGlassEnabled = useUIPreferencesStore((state) => state.tauriGlassEnabled);
  const tauriGlassPreferenceSet = useUIPreferencesStore((state) => state.tauriGlassPreferenceSet);

  useEffect(() => {
    if (!isTauri()) return;

    let disposed = false;
    let observer: MutationObserver | null = null;
    let colorSchemeMediaQuery: MediaQueryList | null = null;

    const getConfig = () => {
      const appIsDarkMode = document.documentElement.classList.contains('dark');
      const systemPrefersDark =
        typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const rootStyles = getComputedStyle(document.documentElement);
      const themeHex = normalizeHexColor(rootStyles.getPropertyValue('--sidebar-bg-hex')) ?? FALLBACK_THEME_HEX;

      const tintBase = appIsDarkMode
        ? darkenHexColor(themeHex, DARK_MODE_DARKEN_FACTOR)
        : blendHexColors(
            themeHex,
            '#ffffff',
            systemPrefersDark
              ? LIGHT_MODE_ON_DARK_SYSTEM_WHITE_BLEND_FACTOR
              : LIGHT_MODE_WHITE_BLEND_FACTOR,
          );
      const tintAlpha = appIsDarkMode
        ? DARK_TINT_ALPHA
        : systemPrefersDark
          ? LIGHT_MODE_ON_DARK_SYSTEM_TINT_ALPHA
          : LIGHT_TINT_ALPHA;

      return {
        enabled: true,
        cornerRadius: 0,
        variant: SIDEBAR_VARIANT,
        tintColor: withAlpha(tintBase, tintAlpha),
      };
    };

    const applyGlassMode = async () => {
      try {
        const [{ invoke }, { getCurrentWindow }] = await Promise.all([
          import('@tauri-apps/api/core'),
          import('@tauri-apps/api/window'),
        ]);
        if (disposed) return;

        const window = getCurrentWindow();
        const supported = await invoke<boolean>('plugin:liquid-glass|is_glass_supported');
        if (disposed) return;

        document.documentElement.setAttribute(
          'data-liquid-glass-supported',
          supported ? 'true' : 'false'
        );

        if (!tauriGlassPreferenceSet) {
          useUIPreferencesStore.getState().setTauriGlassAutoEnabled(supported);
        }

        if (!supported || !tauriGlassEnabled) {
          document.documentElement.setAttribute('data-liquid-glass-active', 'false');
          // Disable the plugin fallback on older macOS to avoid the milky NSVisualEffectView layer.
          await invoke('plugin:liquid-glass|set_liquid_glass_effect', {
            window: window.label,
            config: { enabled: false },
          });
          return;
        }

        await invoke('plugin:liquid-glass|set_liquid_glass_effect', {
          window: window.label,
          config: getConfig(),
        });
        document.documentElement.setAttribute('data-liquid-glass-active', 'true');
      } catch (error) {
        document.documentElement.setAttribute('data-liquid-glass-active', 'false');
        logger.debug('[useTauriLiquidGlass] Failed to configure liquid glass mode:', error);
      }
    };

    void applyGlassMode();
    observer = new MutationObserver(() => {
      void applyGlassMode();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-sidebar-theme'],
    });
    colorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleColorSchemeChange = () => {
      void applyGlassMode();
    };
    colorSchemeMediaQuery.addEventListener('change', handleColorSchemeChange);

    return () => {
      disposed = true;
      observer?.disconnect();
      colorSchemeMediaQuery?.removeEventListener('change', handleColorSchemeChange);
      document.documentElement.removeAttribute('data-liquid-glass-supported');
      document.documentElement.removeAttribute('data-liquid-glass-active');
    };
  }, [tauriGlassEnabled, tauriGlassPreferenceSet]);
}
